import {
  AbsenceType,
  CalendarDayType,
  OnCallMultiplier,
  BenefitGroupType,
  BenefitBuyerType,
  AdditionalPeopleNumber,
  BonusType,
  AbsenceTypeInput,
  VatNotAppliedReason,
  HolidayCalendarId,
  B2BContractType,
  CompanyRole,
  ContractStatus,
  ProgressionLevel,
  Settlement,
} from '../../../schema/admin';
import { EmploymentContractType, Language, Quarter } from '../../../schema/user';
import { CellState } from '../../additionalTypes';

import messages, {
  enumMessage,
  messagesToIdentityMap,
  parsePluralMessages,
  sharedPluralMessages,
  sharedPluralBuildingBlocks,
  parsePluralMessageBlocks,
  type SharedEnumTranslations,
} from '../messages';

import type { DefaultLanguage } from '.';

export const sharedEnumTranslations: SharedEnumTranslations = {
  [enumMessage('cellState', CellState.free)]: 'Free – click to book',
  [enumMessage('cellState', CellState.taken)]: 'Taken',
  [enumMessage('cellState', CellState.pending)]: 'Waiting for approval',
  [enumMessage('cellState', CellState.approved)]: 'Your booking',
  [enumMessage('cellState', CellState.assigned)]: 'Your permanent desk',
  [enumMessage('cellState', CellState.outOfWindow)]: 'Blocked',
  [enumMessage('calendarDayType', CalendarDayType.HOLIDAY)]: 'Public holiday',
  [enumMessage('calendarDayType', CalendarDayType.FREEDAYFOROTHERDAY)]: 'VirtusLab holiday',
  [enumMessage('absenceType', AbsenceType.SICKLEAVE)]: 'Sick leave',
  [enumMessage('absenceType', AbsenceType.SICKFAMILYMEMBERCARELEAVE)]: 'Sick child or family member care leave',
  [enumMessage('absenceType', AbsenceType.ANNUALLEAVE)]: 'Annual leave',
  [enumMessage('absenceType', AbsenceType.UNPAIDLEAVE)]: 'Unpaid leave',
  [enumMessage('absenceType', AbsenceType.SPECIALLEAVE)]: 'Special leave',
  [enumMessage('absenceType', AbsenceType.FLEXTIMELEAVE)]: 'Flextime leave',
  [enumMessage('absenceType', AbsenceType.MATERNITYLEAVE)]: 'Maternity leave',
  [enumMessage('absenceType', AbsenceType.PATERNITYLEAVE)]: 'Paternity leave',
  [enumMessage('absenceType', AbsenceType.PAIDLEAVE)]: 'Paid leave',
  [enumMessage('absenceType', AbsenceType.OUTOFOFFICE)]: 'Out of office',
  [enumMessage('absenceType', AbsenceType.DUETOFORCEMAJEURELEAVE)]: 'Unexpected event leave',
  [enumMessage('absenceType', AbsenceType.CHILDCARELEAVE)]: 'Childcare leave',
  [enumMessage('absenceType', AbsenceType.CAREGIVINGLEAVE)]: 'Caregiving leave',
  [enumMessage('absenceType', AbsenceType.PARENTALLEAVE)]: 'Parental leave',
  [enumMessage('progressionLevel', ProgressionLevel.ENGINEERINTERN)]: 'Intern',
  [enumMessage('progressionLevel', ProgressionLevel.NONENGINEERINTERN)]: 'Intern',
  [enumMessage('progressionLevel', ProgressionLevel.ENGINEERJUNIOR)]: 'Junior',
  [enumMessage('progressionLevel', ProgressionLevel.NONENGINEERJUNIOR)]: 'Junior',
  [enumMessage('progressionLevel', ProgressionLevel.ENGINEERMID)]: 'Mid',
  [enumMessage('progressionLevel', ProgressionLevel.NONENGINEERREGULAR)]: 'Regular',
  [enumMessage('progressionLevel', ProgressionLevel.ENGINEERSENIOR)]: 'Senior',
  [enumMessage('progressionLevel', ProgressionLevel.NONENGINEERSENIOR)]: 'Senior',
  [enumMessage('progressionLevel', ProgressionLevel.ENGINEERSTAFF)]: 'Staff',
  [enumMessage('progressionLevel', ProgressionLevel.NONENGINEEREXPERT)]: 'Expert',
  [enumMessage('progressionLevel', ProgressionLevel.ENGINEERPRINCIPAL)]: 'Principal',
  [enumMessage('progressionLevel', ProgressionLevel.ENGINEERJ1)]: 'J1',
  [enumMessage('progressionLevel', ProgressionLevel.ENGINEERJ2)]: 'J2',
  [enumMessage('progressionLevel', ProgressionLevel.ENGINEERJ3)]: 'J3',
  [enumMessage('progressionLevel', ProgressionLevel.ENGINEERM1)]: 'M1',
  [enumMessage('progressionLevel', ProgressionLevel.ENGINEERM2)]: 'M2',
  [enumMessage('progressionLevel', ProgressionLevel.ENGINEERM3)]: 'M3',
  [enumMessage('progressionLevel', ProgressionLevel.ENGINEERM4)]: 'M4',
  [enumMessage('progressionLevel', ProgressionLevel.ENGINEERS1)]: 'S1',
  [enumMessage('progressionLevel', ProgressionLevel.ENGINEERS2)]: 'S2',
  [enumMessage('progressionLevel', ProgressionLevel.ENGINEERS3)]: 'S3',
  [enumMessage('progressionLevel', ProgressionLevel.ENGINEERS4)]: 'S4',
  [enumMessage('progressionLevel', ProgressionLevel.ENGINEERS5)]: 'S5',
  [enumMessage('progressionLevel', ProgressionLevel.ENGINEERE1)]: 'E1',
  [enumMessage('progressionLevel', ProgressionLevel.ENGINEERE2)]: 'E2',
  [enumMessage('progressionLevel', ProgressionLevel.NONENGINEERJ1)]: 'J1',
  [enumMessage('progressionLevel', ProgressionLevel.NONENGINEERJ2)]: 'J2',
  [enumMessage('progressionLevel', ProgressionLevel.NONENGINEERJ3)]: 'J3',
  [enumMessage('progressionLevel', ProgressionLevel.NONENGINEERM1)]: 'M1',
  [enumMessage('progressionLevel', ProgressionLevel.NONENGINEERM2)]: 'M2',
  [enumMessage('progressionLevel', ProgressionLevel.NONENGINEERM3)]: 'M3',
  [enumMessage('progressionLevel', ProgressionLevel.NONENGINEERM4)]: 'M4',
  [enumMessage('progressionLevel', ProgressionLevel.NONENGINEERS1)]: 'S1',
  [enumMessage('progressionLevel', ProgressionLevel.NONENGINEERS2)]: 'S2',
  [enumMessage('progressionLevel', ProgressionLevel.NONENGINEERS3)]: 'S3',
  [enumMessage('progressionLevel', ProgressionLevel.NONENGINEERS4)]: 'S4',
  [enumMessage('progressionLevel', ProgressionLevel.NONENGINEERE1)]: 'E1',
  [enumMessage('progressionLevel', ProgressionLevel.NONENGINEERE2)]: 'E2',
  [enumMessage('absenceTypeInput', AbsenceTypeInput.SICKLEAVE)]: 'Sick leave',
  [enumMessage('absenceTypeInput', AbsenceTypeInput.SICKFAMILYMEMBERCARELEAVE)]: 'Sick child or family member care leave',
  [enumMessage('absenceTypeInput', AbsenceTypeInput.ANNUALLEAVE)]: 'Annual leave',
  [enumMessage('absenceTypeInput', AbsenceTypeInput.UNPAIDLEAVE)]: 'Unpaid leave',
  [enumMessage('absenceTypeInput', AbsenceTypeInput.SPECIALLEAVE)]: 'Special leave',
  [enumMessage('absenceTypeInput', AbsenceTypeInput.FLEXTIMELEAVE)]: 'Flextime leave',
  [enumMessage('absenceTypeInput', AbsenceTypeInput.MATERNITYLEAVE)]: 'Maternity leave',
  [enumMessage('absenceTypeInput', AbsenceTypeInput.PATERNITYLEAVE)]: 'Paternity leave',
  [enumMessage('absenceTypeInput', AbsenceTypeInput.PAIDLEAVE)]: 'Paid leave',
  [enumMessage('absenceTypeInput', AbsenceTypeInput.OUTOFOFFICE)]: 'Out of office',
  [enumMessage('absenceTypeInput', AbsenceTypeInput.DUETOFORCEMAJEURELEAVE)]: 'Unexpected event leave',
  [enumMessage('absenceTypeInput', AbsenceTypeInput.CHILDCARELEAVE)]: 'Childcare leave',
  [enumMessage('absenceTypeInput', AbsenceTypeInput.CAREGIVINGLEAVE)]: 'Caregiving leave',
  [enumMessage('absenceTypeInput', AbsenceTypeInput.PARENTALLEAVE)]: 'Parental leave',
  [enumMessage('onCallMultiplier', OnCallMultiplier.ONE)]: 'x1',
  [enumMessage('onCallMultiplier', OnCallMultiplier.ONEANDHALF)]: 'x1.5',
  [enumMessage('onCallMultiplier', OnCallMultiplier.THREE)]: 'x3',
  [enumMessage('language', Language.ENGLISH)]: 'English',
  [enumMessage('language', Language.OTHER)]: 'Other',
  [enumMessage('quarter', Quarter.Q1)]: 'Q1',
  [enumMessage('quarter', Quarter.Q2)]: 'Q2',
  [enumMessage('quarter', Quarter.Q3)]: 'Q3',
  [enumMessage('quarter', Quarter.Q4)]: 'Q4',
  [enumMessage('benefitAdditionalPeople', AdditionalPeopleNumber.ONE)]: '1',
  [enumMessage('benefitAdditionalPeople', AdditionalPeopleNumber.TWO)]: '2',
  [enumMessage('benefitAdditionalPeople', AdditionalPeopleNumber.THREE)]: '3',
  [enumMessage('benefitAdditionalPeople', AdditionalPeopleNumber.FOUR)]: '4',
  [enumMessage('benefitAdditionalPeople', AdditionalPeopleNumber.MORE)]: 'more than 4',
  [enumMessage('benefitGroupType', BenefitGroupType.HEALTH)]: 'Health',
  [enumMessage('benefitGroupType', BenefitGroupType.OFFICE)]: 'Office',
  [enumMessage('benefitGroupType', BenefitGroupType.SPORT)]: 'Sport',
  [enumMessage('benefitBuyerType', BenefitBuyerType.PROMISEE)]: 'Promisee',
  [enumMessage('benefitBuyerType', BenefitBuyerType.PROMISOR)]: 'Promisor',
  [enumMessage('benefitBuyerType', BenefitBuyerType.THIRDPARTY)]: '3rd party',
  [enumMessage('bonusType', BonusType.APPRECIATION)]: 'Appreciation bonus',
  [enumMessage('bonusType', BonusType.EMPLOYEEREFERRAL)]: 'Employee referral',
  [enumMessage('bonusType', BonusType.LANGUAGELEARNING)]: 'Language learning',
  [enumMessage('bonusType', BonusType.OTHER)]: 'Other',
  [enumMessage('bonusType', BonusType.TRAININGPACKAGE)]: 'Training package',
  [enumMessage('bonusType', BonusType.REMOTEWORKEQUIPMENT)]: 'Remote work equipment',
  [enumMessage('bonusType', BonusType.BUSINESSTRAVEL)]: 'Business travel',
  [enumMessage('bonusType', BonusType.CLIENTREFERRAL)]: 'Client referral',
  [enumMessage('bonusType', BonusType.CONFERENCE)]: 'Conference',
  [enumMessage('bonusType', BonusType.COSTRECHARGE)]: 'Cost recharge',
  [enumMessage('bonusType', BonusType.SALESCOMMISSIONS)]: 'Sales commissions',
  [enumMessage('bonusType', BonusType.SEVERANCEPAY)]: 'Severance pay',
  [enumMessage('bonusType', BonusType.SHAREHOLDERDIVIDEND)]: 'Shareholder dividend',
  [enumMessage('bonusType', BonusType.TRAININGPACKAGEEXTERNALDEDUCTIONVALUE)]: 'Training package deduction',
  [enumMessage('vatNotAppliedReason', VatNotAppliedReason.EXEMPTFROMVAT)]: 'Exempt from VAT',
  [enumMessage('vatNotAppliedReason', VatNotAppliedReason.VATNOTAPPLICABLE)]: 'VAT not applicable',
  [enumMessage('holidayCalendaryId', HolidayCalendarId.PL)]: 'Poland',
  [enumMessage('holidayCalendaryId', HolidayCalendarId.SE)]: 'Sweden',
  [enumMessage('holidayCalendaryId', HolidayCalendarId.DE_BW)]: 'Germany - Bavaria',
  [enumMessage('holidayCalendaryId', HolidayCalendarId.DE_HE)]: 'Germany - Hesse',
  [enumMessage('holidayCalendaryId', HolidayCalendarId.CH_BERN)]: 'Switzerland - Bern',
  [enumMessage('holidayCalendaryId', HolidayCalendarId.CH_ZURYCH)]: 'Switzerland - Zürich',
  [enumMessage('holidayCalendaryId', HolidayCalendarId.UK_ENG)]: 'United Kingdom',
  [enumMessage('companyRole', CompanyRole.CHIEFOFFICER)]: 'C-level',
  [enumMessage('companyRole', CompanyRole.DIRECTOR)]: 'Director',
  [enumMessage('companyRole', CompanyRole.HEAD)]: 'Head of',
  [enumMessage('companyRole', CompanyRole.MANAGER)]: 'Manager',
  [enumMessage('companyRole', CompanyRole.LEAD)]: 'Lead',
  [enumMessage('companyRole', CompanyRole.PERSONNEL)]: 'Personnel',
  [enumMessage('employmentContractType', EmploymentContractType.UOP)]: 'UOP',
  [enumMessage('employmentContractType', EmploymentContractType.UOD)]: 'UOD',
  [enumMessage('employmentContractType', EmploymentContractType.UZ)]: 'UZ',
  [enumMessage('employmentContractType', EmploymentContractType.MANAGEMENT)]: 'Management',
  [enumMessage('b2bContractType', B2BContractType.B2B)]: 'B2B',
  [enumMessage('b2bContractType', B2BContractType.SUBCONTRACT)]: 'Sub',
  [enumMessage('settlement', Settlement.DAILY)]: 'Daily',
  [enumMessage('settlement', Settlement.HOURLY)]: 'Hourly',
  [enumMessage('settlement', Settlement.MONTHLY)]: 'Monthly',
  [enumMessage('contractStatus', ContractStatus.ACTIVE)]: 'Active',
  [enumMessage('contractStatus', ContractStatus.TERMINATED)]: 'Terminated',
  [enumMessage('contractStatus', ContractStatus.CONCLUDED)]: 'Concluded',
};

const translations: DefaultLanguage = {
  ...messagesToIdentityMap(messages),
  ...parsePluralMessages(sharedPluralMessages),
  ...parsePluralMessageBlocks(sharedPluralBuildingBlocks),
  ...sharedEnumTranslations,
} as const;

export default translations;
